import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const Navbar = ({ onSignInClick }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCoursesOpen, setIsCoursesOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const courses = [
    { title: 'Linux Fundamentals', desc: 'Master command line and system administration', id: 'linux-fundamentals' },
    { title: 'Bash Scripting', desc: 'Automate tasks and workflows', id: 'bash-scripting' },
    { title: 'Git & GitHub', desc: 'Version control and collaboration', id: 'git-github' },
    { title: 'Cloud Basics', desc: 'AWS, Azure, and GCP essentials', id: 'cloud-basics' },
    { title: 'Infrastructure as Code', desc: 'Terraform and Pulumi mastery', id: 'infrastructure-as-code' },
    { title: 'Containerization', desc: 'Docker and container orchestration', id: 'containerization' },
    { title: 'Kubernetes', desc: 'Production-grade container management', id: 'kubernetes' },
    { title: 'CI/CD Pipelines', desc: 'Automated deployment workflows', id: 'cicd-pipelines' },
    { title: 'Observability', desc: 'Monitoring and logging systems', id: 'observability' }
  ];

  const isActive = (path) => location.pathname === path;

  const handleSignInClick = () => {
    if (onSignInClick) {
      onSignInClick();
    } else {
      // If no callback provided, navigate to landing page with auth param
      navigate('/?auth=true');
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-sm' : 'bg-white'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <div className="text-2xl font-semibold tracking-tight text-[#1E293B]">
              Peabsmart <span className="text-[#0EA5E9]">DevOps</span> Academy
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <Link
              to="/about"
              className={`text-[15px] font-medium transition-colors relative ${
                isActive('/about')
                  ? 'text-[#0EA5E9]'
                  : 'text-[#475569] hover:text-[#1E293B]'
              }`}
            >
              About Us
              {isActive('/about') && (
                <span className="absolute bottom-[-8px] left-0 right-0 h-[2px] bg-[#0EA5E9]"></span>
              )}
            </Link>

            {/* Courses Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setIsCoursesOpen(true)}
              onMouseLeave={() => setIsCoursesOpen(false)}
            >
              <button
                className={`flex items-center gap-1 text-[15px] font-medium transition-colors ${
                  isActive('/courses')
                    ? 'text-[#0EA5E9]'
                    : 'text-[#475569] hover:text-[#1E293B]'
                }`}
              >
                Courses
                <ChevronDown
                  className={`w-4 h-4 transition-transform ${
                    isCoursesOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {/* Dropdown Menu */}
              {isCoursesOpen && (
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 w-[600px] bg-white rounded-sm shadow-lg border border-[#E2E8F0] animate-fadeSlideDown">
                  <div className="p-6">
                    <div className="grid grid-cols-2 gap-4">
                      {courses.map((course, idx) => (
                        <Link
                          key={idx}
                          to={`/courses#${course.id}`}
                          className="p-3 rounded-sm hover:bg-[#F8FAFC] transition-colors group"
                        >
                          <div className="font-semibold text-[#1E293B] text-[14px] group-hover:text-[#0EA5E9] transition-colors">
                            {course.title}
                          </div>
                          <div className="text-[13px] text-[#64748B] mt-1">
                            {course.desc}
                          </div>
                        </Link>
                      ))}
                    </div>
                    <div className="border-t border-[#E2E8F0] mt-4 pt-4">
                      <Link
                        to="/courses"
                        className="flex items-center justify-center w-full py-2 text-[14px] font-medium text-[#0EA5E9] border border-[#0EA5E9] rounded-sm hover:bg-[#0EA5E9] hover:text-white transition-colors"
                      >
                        View All Courses
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/career-launch"
              className={`text-[15px] font-medium transition-colors relative ${
                isActive('/career-launch')
                  ? 'text-[#0EA5E9]'
                  : 'text-[#475569] hover:text-[#1E293B]'
              }`}
            >
              Career Launch
              {isActive('/career-launch') && (
                <span className="absolute bottom-[-8px] left-0 right-0 h-[2px] bg-[#0EA5E9]"></span>
              )}
            </Link>

            <Link
              to="/contact"
              className={`text-[15px] font-medium transition-colors relative ${
                isActive('/contact')
                  ? 'text-[#0EA5E9]'
                  : 'text-[#475569] hover:text-[#1E293B]'
              }`}
            >
              Contact
              {isActive('/contact') && (
                <span className="absolute bottom-[-8px] left-0 right-0 h-[2px] bg-[#0EA5E9]"></span>
              )}
            </Link>

            {user ? (
              <Link
                to="/dashboard"
                className="ml-4 px-6 py-2.5 bg-[#0EA5E9] text-white text-[14px] font-medium rounded-sm hover:bg-[#0284C7] transition-colors"
              >
                Dashboard
              </Link>
            ) : (
              <button
                onClick={handleSignInClick}
                className="ml-4 px-6 py-2.5 bg-[#0EA5E9] text-white text-[14px] font-medium rounded-sm hover:bg-[#0284C7] transition-colors"
                data-testid="navbar-signin-btn"
              >
                Sign In
              </button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-[#475569] hover:text-[#1E293B]"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-[#E2E8F0]">
            <div className="flex flex-col space-y-4">
              <Link
                to="/about"
                className={`text-[15px] font-medium ${
                  isActive('/about') ? 'text-[#0EA5E9]' : 'text-[#475569]'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                About Us
              </Link>

              {/* Mobile Courses Accordion */}
              <div>
                <button
                  className="flex items-center justify-between w-full text-[15px] font-medium text-[#475569]"
                  onClick={() => setIsCoursesOpen(!isCoursesOpen)}
                >
                  Courses
                  <ChevronDown
                    className={`w-4 h-4 transition-transform ${
                      isCoursesOpen ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {isCoursesOpen && (
                  <div className="mt-3 ml-4 space-y-3">
                    {courses.map((course, idx) => (
                      <Link
                        key={idx}
                        to={`/courses#${course.id}`}
                        className="block"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <div className="font-semibold text-[#1E293B] text-[14px]">
                          {course.title}
                        </div>
                        <div className="text-[13px] text-[#64748B]">{course.desc}</div>
                      </Link>
                    ))}
                    <Link
                      to="/courses"
                      className="block text-[14px] font-medium text-[#0EA5E9]"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      View All Courses →
                    </Link>
                  </div>
                )}
              </div>

              <Link
                to="/career-launch"
                className={`text-[15px] font-medium ${
                  isActive('/career-launch') ? 'text-[#0EA5E9]' : 'text-[#475569]'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Career Launch
              </Link>

              <Link
                to="/contact"
                className={`text-[15px] font-medium ${
                  isActive('/contact') ? 'text-[#0EA5E9]' : 'text-[#475569]'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Contact
              </Link>

              {user ? (
                <Link
                  to="/dashboard"
                  className="inline-block px-6 py-2.5 bg-[#0EA5E9] text-white text-[14px] font-medium rounded-sm text-center"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Dashboard
                </Link>
              ) : (
                <button
                  onClick={handleSignInClick}
                  className="inline-block px-6 py-2.5 bg-[#0EA5E9] text-white text-[14px] font-medium rounded-sm text-center"
                >
                  Sign In
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        @keyframes fadeSlideDown {
          from {
            opacity: 0;
            transform: translateX(-50%) translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }
        }
        .animate-fadeSlideDown {
          animation: fadeSlideDown 0.2s ease-out;
        }
      `}</style>
    </nav>
  );
};

export default Navbar;
